import { useEffect } from "react";
import { Dialog, DialogContent, Typography, Zoom } from "@mui/material";
import { INVOICE_ACTIONS, EVENTS, KEYBOARD_KEYS, SALE_DIALOG_STATUS } from "const";
import { eventsEmitter } from "utils/events";
import styles from "./styles.module.css";

export default function SaleStatusDialog({ open, status = SALE_DIALOG_STATUS.SUCCESS, action, onClose }) {
  const isProcessing = status === SALE_DIALOG_STATUS.PROCESSING;

  useEffect(() => {
    if (open && !isProcessing) {
      const timer = setTimeout(() => {
        onClose();
      }, 1000); // Fast 1-second auto close on success
      return () => clearTimeout(timer);
    }
  }, [open, isProcessing, onClose]);

  // Handle Enter / Escape key
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (isProcessing) {
        // While processing, swallow Enter and Escape to prevent duplicate actions or accidental exit
        if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.ESCAPE) {
          e.preventDefault?.();
          e.stopPropagation?.();
        }
      } else {
        // When completed, Enter or Escape closes immediately
        if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.ESCAPE) {
          e.preventDefault?.();
          e.stopPropagation?.();
          onClose();
        }
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [open, isProcessing, onClose]);

  const getTitle = () => {
    if (isProcessing) {
      switch (action) {
        case INVOICE_ACTIONS.SAVE:
          return "Saving Invoice...";
        case INVOICE_ACTIONS.SAVE_AND_WHATSAPP:
          return "Saving & Sending WhatsApp...";
        case INVOICE_ACTIONS.PRINT_AND_WHATSAPP:
          return "Printing & Sending WhatsApp...";
        case INVOICE_ACTIONS.WITH_KOT:
          return "Printing KOT & Invoice...";
        default:
          return "Printing Invoice...";
      }
    }

    switch (action) {
      case INVOICE_ACTIONS.SAVE:
        return "Invoice Saved!";
      case INVOICE_ACTIONS.SAVE_AND_WHATSAPP:
        return "Saved & WhatsApp Sent!";
      case INVOICE_ACTIONS.PRINT_AND_WHATSAPP:
        return "Printed & WhatsApp Sent!";
      default:
        return "Sale Completed!";
    }
  };

  const getMessage = () => {
    if (isProcessing) {
      return "Please wait while the transaction is being processed...";
    }
    return "The transaction has been successfully processed and finalized.";
  };

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onClose}
      TransitionComponent={Zoom}
      transitionDuration={350}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={isProcessing}
      PaperProps={{
        className: styles.dialogPaper,
      }}
    >
      <DialogContent className={styles.dialogContent}>
        {isProcessing ? (
          <div className={styles.processingIconWrapper}>
            <div className={styles.spinner} />
          </div>
        ) : (
          <div className={styles.successIconWrapper}>
            <svg className={styles.checkmark} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle className={styles.checkmarkCircle} cx="26" cy="26" r="25" fill="none" />
              <path className={styles.checkmarkCheck} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          </div>
        )}

        <Typography variant="h5" className={styles.title}>
          {getTitle()}
        </Typography>

        <Typography variant="body1" className={styles.message}>
          {getMessage()}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
